/**
 * Normalizes a DICOM Person Name (PN) value read through dcmjs to a plain
 * string. Depending on how the dataset was produced, dcmjs renders PN
 * attributes as:
 *
 * - a plain string,
 * - a **boxed `String` subclass** (assigning through a naturalized
 *   dataset's PN accessor yields this; `typeof` is `'object'`,
 *   `Array.isArray` is false, and its custom `toJSON` misleadingly
 *   prints `[{ Alphabetic: ... }]`),
 * - a `{ Alphabetic: '...' }` object, or
 * - an array of any of the above.
 *
 * Anything else normalizes to an empty string rather than leaking a
 * non-string into code typed `string` — serialized into an API request
 * that fails the backend's string binding (the save workflow's approval
 * call hit exactly that).
 */
export function personNameToString(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }
    if (value === null || value === undefined) {
        return '';
    }
    if (value instanceof String) {
        // dcmjs's PN wrapper subclasses String and OVERRIDES toString()
        // to return its component-group form (an object), so a plain
        // value.toString() / String(value) still yields a non-string.
        // The built-in prototype method reads the internal string data
        // and cannot be overridden away.
        return String.prototype.toString.call(value);
    }
    if (Array.isArray(value)) {
        return value.length > 0 ? personNameToString(value[0]) : '';
    }
    if (typeof value === 'object' && 'Alphabetic' in value) {
        return personNameToString(
            (value as { Alphabetic?: unknown }).Alphabetic
        );
    }
    return '';
}
